/**
 * Utility functions for user role management
 */

/**
 * Check if user is an admin
 * @param {Object} user - User object
 * @returns {boolean} - True if user is admin
 */
export const isAdmin = (user) => {
  return user?.is_admin || false;
};

/**
 * Check if user is a coach in any training group
 * @param {Object} user - User object
 * @returns {boolean} - True if user is coach in any training group
 */
export const isCoach = (user) => {
  return (
    user?.training_group_memberships?.some((membership) => membership.role_in_group === 'coach') ||
    false
  );
};

/**
 * Check if user is a coach in a specific training group
 * @param {Object} user - User object
 * @param {number} trainingGroupId - Training group ID
 * @returns {boolean} - True if user is coach in the specified training group
 */
export const isCoachInTrainingGroup = (user, trainingGroupId) => {
  return (
    user?.training_group_memberships?.some(
      (membership) =>
        membership.training_group_id === trainingGroupId && membership.role_in_group === 'coach'
    ) || false
  );
};

/**
 * Check if user is a coach or admin
 * @param {Object} user - User object
 * @returns {boolean} - True if user is coach or admin
 */
export const isCoachOrAdmin = (user) => {
  return isAdmin(user) || isCoach(user);
};

/**
 * Get user's effective role
 * @param {Object} user - User object
 * @returns {string} - 'admin', 'coach', or 'rower'
 */
export const getUserRole = (user) => {
  if (isAdmin(user)) return 'admin';
  if (isCoach(user)) return 'coach';
  return 'rower';
};

/**
 * Check if user can modify content (admin or coach)
 * @param {Object} user - User object
 * @returns {boolean} - True if user can modify content
 */
export const canModifyContent = (user) => {
  return isCoachOrAdmin(user);
};

/**
 * Check if user can access admin features
 * @param {Object} user - User object
 * @returns {boolean} - True if user can access admin features
 */
export const canAccessAdmin = (user) => {
  return isAdmin(user);
};

/**
 * Get user's training group memberships
 * @param {Object} user - User object
 * @returns {Array} - Array of training group memberships
 */
export const getUserTrainingGroups = (user) => {
  return user?.training_group_memberships || [];
};

/**
 * Get training groups where user is a coach
 * @param {Object} user - User object
 * @returns {Array} - Array of training group IDs where user is coach
 */
export const getCoachingTrainingGroups = (user) => {
  return getUserTrainingGroups(user)
    .filter((membership) => membership.role_in_group === 'coach')
    .map((membership) => membership.training_group_id);
};

/**
 * Check if user can manage a specific training group
 * @param {Object} user - User object
 * @param {number} trainingGroupId - Training group ID
 * @returns {boolean} - True if user can manage the training group
 */
export const canManageTrainingGroup = (user, trainingGroupId) => {
  return isAdmin(user) || isCoachInTrainingGroup(user, trainingGroupId);
};

/**
 * Check if user can manage a specific training group (coaches only, no admin override)
 * @param {Object} user - User object
 * @param {number} trainingGroupId - Training group ID
 * @returns {boolean} - True if user is a coach in the specific training group
 */
export const canManageTrainingGroupAsCoach = (user, trainingGroupId) => {
  return isCoachInTrainingGroup(user, trainingGroupId);
};

/**
 * Check if user can send notifications to a training group
 * @param {Object} user - User object
 * @param {number} trainingGroupId - Training group ID
 * @returns {boolean} - True if user can send notifications to the training group
 */
export const canSendNotificationsToGroup = (user, trainingGroupId) => {
  return canManageTrainingGroup(user, trainingGroupId);
};

/**
 * Check if user can send system-wide notifications
 * @param {Object} user - User object
 * @returns {boolean} - True if user can send system-wide notifications
 */
export const canSendSystemNotifications = (user) => {
  return isAdmin(user);
};

/**
 * Check if user can create training sessions
 * @param {Object} user - User object
 * @returns {boolean} - True if user can create training sessions
 */
export const canCreateTrainingSessions = (user) => {
  return isCoachOrAdmin(user);
};

/**
 * Check if user can create crews
 * @param {Object} user - User object
 * @returns {boolean} - True if user can create crews
 */
export const canCreateCrews = (user) => {
  return isCoach(user);
};

/**
 * Check if user can edit crews
 * @param {Object} user - User object
 * @returns {boolean} - True if user can edit crews
 */
export const canEditCrews = (user) => {
  return isCoach(user);
};

/**
 * Get all training group IDs user belongs to (regardless of role)
 * @param {Object} user - User object
 * @returns {Array} - Array of training group IDs
 */
export const getAllUserTrainingGroupIds = (user) => {
  return getUserTrainingGroups(user)
    .filter(
      (membership) => membership.role_in_group === 'coach' || membership.role_in_group === 'rower'
    )
    .map((membership) => membership.training_group_id);
};

/**
 * Check if user can manage any of a list of training groups (for session RBAC)
 * @param {Object} user - User object
 * @param {Array<number>} trainingGroupIds - Array of training group IDs
 * @returns {boolean} - True if user can manage any of the groups
 */
export const canManageAnyTrainingGroup = (user, trainingGroupIds) => {
  if (isAdmin(user)) return true;
  return trainingGroupIds.some((groupId) => isCoachInTrainingGroup(user, groupId));
};

/**
 * Check if data is accessible to user based on their training group memberships (accepts array)
 * @param {Object} user - User object
 * @param {number|Array} trainingGroupIds - Training group ID or array of IDs
 * @returns {boolean} - True if user has access
 */
export const hasAccessToTrainingGroups = (user, trainingGroupIds) => {
  if (isAdmin(user)) return true;

  const userGroupIds = getAllUserTrainingGroupIds(user);
  const targetGroupIds = Array.isArray(trainingGroupIds) ? trainingGroupIds : [trainingGroupIds];

  return targetGroupIds.some((groupId) => userGroupIds.includes(groupId));
};
